// Meltdown — a brand-new template set (not a remix of the existing tiers).
//
// Built from the same four primitives (B block, S spike, L vertical laser, p
// horizontal projectile spawner) but arranged into mechanics the base game
// never uses, while keeping the same start/end-height chaining contract and
// difficulty ramp. (The directional variants `l`/`P` exist but aren't used here.)
//
// Signature mechanics:
//   * Laser totems / gated doorways — red beams on posts (hop + time) and, in
//     the harder tiers, a too-tall-to-jump block wall with a laser-filled
//     opening you run on the lasers' synchronized off-pulse.
//   * Multi-height projectile crossfire — two bullet lanes at once; thread a
//     measured hop through the safe band between them.
//   * Bullets across a chasm — a turret firing over a gap at jump-apex height.
//   * Spiked / terraced / gapped climbs — varied height transitions.
//
// Even level1 leads with these (lasers, turrets, totems, terraces) so the map
// reads as distinctly Meltdown from the very first scenes — it is NOT the same
// spikes/pits/stairs vocabulary as the classic easy tier.
//
// Authoring rules baked into every template here:
//   1. HEIGHT CONVENTION (so scenes tile seamlessly):
//        low edge  = ground only on rows 18-19 (surface at row 18)
//        high edge = a block column filling rows 8-19 (surface at row 8)
//      Transitions ramp between the two across the 30-wide scene, with varied
//      shapes (terraces, gap-jumps, terraced drops) rather than one staircase.
//   2. SPIKES ARE NEVER STACKED. A spike always sits on a block (or the ground)
//      directly below it; an elevated spike is a single S on a B post.
//   3. BULLET LANES ARE CONTAINED. Every spawner fires into a backstop block
//      within the same scene, so its bullets terminate inside this tile.
//   4. BULLETS NEVER CROSS A SPIKE on the spawner's firing row.
//   5. EASY (level1) STAYS EASY. Single-idea scenes only — no bullets-with-
//      spikes and no precise spiked climbs; those live in the harder tiers.
//
// Grids are 30 wide x 20 tall; defineProceduralMap validates that at load.

export const meltdownTiers = {
  // --- level1: gentle but unmistakably Meltdown (lasers/turrets/totems) ----
  level1: {
    // Two "laser totems": a red beam on a low post. Hop each, timed to the
    // beams' off-pulse. The signature Meltdown look, in its gentlest form.
    lasertotem1: {
      layout: `
..............................
..............................
..............................
..............................
..............................
..............................
..............................
..............................
..............................
..............................
..............................
..............................
..............................
..............................
..............................
..............................
...........L.......L..........
...........B.......B..........
BBBBBBBBBBBBBBBBBBBBBBBBBBBBBB
BBBBBBBBBBBBBBBBBBBBBBBBBBBBBB
`,
      metadata: { startsHigh: false, endsHigh: false },
    },

    // A lone contained bullet turret: jump the incoming rounds, jump the end
    // blocks. A left backstop catches the rounds so they die in this tile.
    bullets1: {
      layout: `
..............................
..............................
..............................
..............................
..............................
..............................
..............................
..............................
..............................
..............................
..............................
..............................
..............................
..............................
..............................
..............................
..BB......................BB..
..BB......................pB..
BBBBBBBBBBBBBBBBBBBBBBBBBBBBBB
BBBBBBBBBBBBBBBBBBBBBBBBBBBBBB
`,
      metadata: { startsHigh: false, endsHigh: false },
    },

    // Two "spike totems": a single spike on a low post (never stacked spikes).
    // The melee counterpart to the laser totems.
    totems1: {
      layout: `
..............................
..............................
..............................
..............................
..............................
..............................
..............................
..............................
..............................
..............................
..............................
..............................
..............................
..............................
..............................
..............................
...........S.......S..........
...........B.......B..........
BBBBBBBBBBBBBBBBBBBBBBBBBBBBBB
BBBBBBBBBBBBBBBBBBBBBBBBBBBBBB
`,
      metadata: { startsHigh: false, endsHigh: false },
    },

    // One of each totem side by side — laser then spike — so the map's two
    // emitter types both show up early.
    mixed1: {
      layout: `
..............................
..............................
..............................
..............................
..............................
..............................
..............................
..............................
..............................
..............................
..............................
..............................
..............................
..............................
..............................
..............................
..........L.........S.........
..........B.........B.........
BBBBBBBBBBBBBBBBBBBBBBBBBBBBBB
BBBBBBBBBBBBBBBBBBBBBBBBBBBBBB
`,
      metadata: { startsHigh: false, endsHigh: false },
    },

    // Climb three chunky terraces (not a uniform staircase) up to the high
    // level. Gentle steps; the shape is the distinctive part.
    terraceup1: {
      layout: `
..............................
..............................
..............................
..............................
..............................
..............................
..............................
..............................
.......................BBBBBBB
.......................BBBBBBB
.......................BBBBBBB
................BBBBBBBBBBBBBB
................BBBBBBBBBBBBBB
................BBBBBBBBBBBBBB
........BBBBBBBBBBBBBBBBBBBBBB
........BBBBBBBBBBBBBBBBBBBBBB
........BBBBBBBBBBBBBBBBBBBBBB
........BBBBBBBBBBBBBBBBBBBBBB
BBBBBBBBBBBBBBBBBBBBBBBBBBBBBB
BBBBBBBBBBBBBBBBBBBBBBBBBBBBBB
`,
      metadata: { startsHigh: false, endsHigh: true },
    },

    // Drop down three chunky terraces back to the low level.
    terracedown1: {
      layout: `
..............................
..............................
..............................
..............................
..............................
..............................
..............................
..............................
BBBBBBB.......................
BBBBBBB.......................
BBBBBBB.......................
BBBBBBBBBBBBBB................
BBBBBBBBBBBBBB................
BBBBBBBBBBBBBB................
BBBBBBBBBBBBBBBBBBBBBB........
BBBBBBBBBBBBBBBBBBBBBB........
BBBBBBBBBBBBBBBBBBBBBB........
BBBBBBBBBBBBBBBBBBBBBB........
BBBBBBBBBBBBBBBBBBBBBBBBBBBBBB
BBBBBBBBBBBBBBBBBBBBBBBBBBBBBB
`,
      metadata: { startsHigh: true, endsHigh: false },
    },

    // A spike totem standing on a high plateau. Stays high.
    hightotem1: {
      layout: `
..............................
..............................
..............................
..............................
..............................
..............................
...............S..............
...............B..............
BBBBBBBBBBBBBBBBBBBBBBBBBBBBBB
BBBBBBBBBBBBBBBBBBBBBBBBBBBBBB
BBBBBBBBBBBBBBBBBBBBBBBBBBBBBB
BBBBBBBBBBBBBBBBBBBBBBBBBBBBBB
BBBBBBBBBBBBBBBBBBBBBBBBBBBBBB
BBBBBBBBBBBBBBBBBBBBBBBBBBBBBB
BBBBBBBBBBBBBBBBBBBBBBBBBBBBBB
BBBBBBBBBBBBBBBBBBBBBBBBBBBBBB
BBBBBBBBBBBBBBBBBBBBBBBBBBBBBB
BBBBBBBBBBBBBBBBBBBBBBBBBBBBBB
BBBBBBBBBBBBBBBBBBBBBBBBBBBBBB
BBBBBBBBBBBBBBBBBBBBBBBBBBBBBB
`,
      metadata: { startsHigh: true, endsHigh: true },
    },
  },

  // --- level2: the laser gate, taller spike totems, big terraces -----------
  level2: {
    // Laser-gated doorway: the wall is far too tall to jump, so time the gap.
    gate_b2: {
      layout: `
..............................
..............................
..............................
..............................
..............................
..............BB..............
..............BB..............
..............BB..............
..............BB..............
..............BB..............
..............BB..............
..............BB..............
..............BB..............
..............BB..............
..............BB..............
..............BB..............
..............LL..............
..............LL..............
BBBBBBBBBBBBBBBBBBBBBBBBBBBBBB
BBBBBBBBBBBBBBBBBBBBBBBBBBBBBB
`,
      metadata: { startsHigh: false, endsHigh: false },
    },

    // Taller spike totems than level1: a single spike on a two-tall post (never
    // stacked spikes), so the hop has to clear more.
    spikes_b2: {
      layout: `
..............................
..............................
..............................
..............................
..............................
..............................
..............................
..............................
..............................
..............................
..............................
..............................
..............................
..............................
..............................
.........S.........S..........
.........B.........B..........
.........B.........B..........
BBBBBBBBBBBBBBBBBBBBBBBBBBBBBB
BBBBBBBBBBBBBBBBBBBBBBBBBBBBBB
`,
      metadata: { startsHigh: false, endsHigh: false },
    },

    // Climb via three big terraces instead of a uniform staircase. Ends high.
    terraceup_b2: {
      layout: `
..............................
..............................
..............................
..............................
..............................
..............................
..............................
..............................
......................BBBBBBBB
......................BBBBBBBB
......................BBBBBBBB
...............BBBBBBBBBBBBBBB
...............BBBBBBBBBBBBBBB
...............BBBBBBBBBBBBBBB
........BBBBBBBBBBBBBBBBBBBBBB
........BBBBBBBBBBBBBBBBBBBBBB
........BBBBBBBBBBBBBBBBBBBBBB
........BBBBBBBBBBBBBBBBBBBBBB
BBBBBBBBBBBBBBBBBBBBBBBBBBBBBB
BBBBBBBBBBBBBBBBBBBBBBBBBBBBBB
`,
      metadata: { startsHigh: false, endsHigh: true },
    },

    // Contained bullet lane with a gap to hop in the middle.
    bullets_b2: {
      layout: `
..............................
..............................
..............................
..............................
..............................
..............................
..............................
..............................
..............................
..............................
..............................
..............................
..............................
..............................
..............................
..............................
..BB......................BB..
..BB......................pB..
BBBBBBBBBBBBBB...BBBBBBBBBBBBB
BBBBBBBBBBBBBB...BBBBBBBBBBBBB
`,
      metadata: { startsHigh: false, endsHigh: false },
    },

    // Descend via three big terraces. Starts high, ends low.
    terracedown_b2: {
      layout: `
..............................
..............................
..............................
..............................
..............................
..............................
..............................
..............................
BBBBBBBB......................
BBBBBBBB......................
BBBBBBBB......................
BBBBBBBBBBBBBBB...............
BBBBBBBBBBBBBBB...............
BBBBBBBBBBBBBBB...............
BBBBBBBBBBBBBBBBBBBBBB........
BBBBBBBBBBBBBBBBBBBBBB........
BBBBBBBBBBBBBBBBBBBBBB........
BBBBBBBBBBBBBBBBBBBBBB........
BBBBBBBBBBBBBBBBBBBBBBBBBBBBBB
BBBBBBBBBBBBBBBBBBBBBBBBBBBBBB
`,
      metadata: { startsHigh: true, endsHigh: false },
    },
  },

  // --- level3: doubled gates, crossfire, gap-climbs -----------------------
  level3: {
    // Two laser gates with a single spike island between them.
    doublegate_c3: {
      layout: `
..............................
..............................
..............................
..............................
..............................
.........BB........BB.........
.........BB........BB.........
.........BB........BB.........
.........BB........BB.........
.........BB........BB.........
.........BB........BB.........
.........BB........BB.........
.........BB........BB.........
.........BB........BB.........
.........BB........BB.........
.........BB........BB.........
.........LL........LL.........
.........LL....S...LL.........
BBBBBBBBBBBBBBBBBBBBBBBBBBBBBB
BBBBBBBBBBBBBBBBBBBBBBBBBBBBBB
`,
      metadata: { startsHigh: false, endsHigh: false },
    },

    // Stacked crossfire: a low lane (row 17) you hop and a high lane (row 13)
    // that punishes over-jumping. Both lanes have left backstops so the rounds
    // terminate in this tile; the safe band is the gap between them.
    crossfire_c3: {
      layout: `
..............................
..............................
..............................
..............................
..............................
..............................
..............................
..............................
..............................
..............................
..............................
..............................
..............................
..BBB....................pB...
..............................
..............................
..BB..........................
..BB.....................pB...
BBBBBBBBBBBBBBBBBBBBBBBBBBBBBB
BBBBBBBBBBBBBBBBBBBBBBBBBBBBBB
`,
      metadata: { startsHigh: false, endsHigh: false },
    },

    // A laser gate immediately followed by a gap to clear.
    gatepit_c3: {
      layout: `
..............................
..............................
..............................
..............................
..............................
..........BB..................
..........BB..................
..........BB..................
..........BB..................
..........BB..................
..........BB..................
..........BB..................
..........BB..................
..........BB..................
..........BB..................
..........BB..................
..........LL..................
..........LL..................
BBBBBBBBBBBBBBBBBB...BBBBBBBBB
BBBBBBBBBBBBBBBBBB...BBBBBBBBB
`,
      metadata: { startsHigh: false, endsHigh: false },
    },

    // A climb with a real gap to clear partway up: step up, jump the pit at the
    // mid level, step up again. Ends high.
    climbgap_c3: {
      layout: `
..............................
..............................
..............................
..............................
..............................
..............................
..............................
..............................
......................BBBBBBBB
......................BBBBBBBB
......................BBBBBBBB
......................BBBBBBBB
......................BBBBBBBB
.......BBBBBB...BBBBBBBBBBBBBB
.......BBBBBB...BBBBBBBBBBBBBB
.......BBBBBB...BBBBBBBBBBBBBB
.......BBBBBB...BBBBBBBBBBBBBB
.......BBBBBB...BBBBBBBBBBBBBB
BBBBBBBBBBBBB...BBBBBBBBBBBBBB
BBBBBBBBBBBBB...BBBBBBBBBBBBBB
`,
      metadata: { startsHigh: false, endsHigh: true },
    },

    // A raised island split by a fall-through chasm, with a spike on the far
    // ledge. Stays high.
    island_c3: {
      layout: `
..............................
..............................
..............................
..............................
..............................
..............................
..............................
.....................S........
BBBBBBBBBBBBB....BBBBBBBBBBBBB
BBBBBBBBBBBBB....BBBBBBBBBBBBB
BBBBBBBBBBBBB....BBBBBBBBBBBBB
BBBBBBBBBBBBB....BBBBBBBBBBBBB
BBBBBBBBBBBBB....BBBBBBBBBBBBB
BBBBBBBBBBBBB....BBBBBBBBBBBBB
BBBBBBBBBBBBB....BBBBBBBBBBBBB
BBBBBBBBBBBBB....BBBBBBBBBBBBB
BBBBBBBBBBBBB....BBBBBBBBBBBBB
BBBBBBBBBBBBB....BBBBBBBBBBBBB
BBBBBBBBBBBBB....BBBBBBBBBBBBB
BBBBBBBBBBBBB....BBBBBBBBBBBBB
`,
      metadata: { startsHigh: true, endsHigh: true },
    },
  },

  // --- level4: gates + fire + gaps combined -------------------------------
  level4: {
    // Time a laser gate, hop the backstop block, then dodge a contained bullet
    // lane on the far side. The lane is spike-free and backstopped right after
    // the gate so the rounds die in this tile.
    gauntlet_d4: {
      layout: `
..............................
..............................
..............................
..............................
..............................
........BB....................
........BB....................
........BB....................
........BB....................
........BB....................
........BB....................
........BB....................
........BB....................
........BB....................
........BB....................
........BB....................
........LL..BB.............B..
........LL..BB............pB..
BBBBBBBBBBBBBBBBBBBBBBBBBBBBBB
BBBBBBBBBBBBBBBBBBBBBBBBBBBBBB
`,
      metadata: { startsHigh: false, endsHigh: false },
    },

    // A high chasm with a turret firing across it at jump-apex height — the leap
    // has to thread a volley. The lip on the take-off side backstops the rounds.
    chasmcross_d4: {
      layout: `
..............................
..............................
..............................
..............................
..............................
..............................
..............................
............B.....p...........
BBBBBBBBBBBBB.....BBBBBBBBBBBB
BBBBBBBBBBBBB.....BBBBBBBBBBBB
BBBBBBBBBBBBB.....BBBBBBBBBBBB
BBBBBBBBBBBBB.....BBBBBBBBBBBB
BBBBBBBBBBBBB.....BBBBBBBBBBBB
BBBBBBBBBBBBB.....BBBBBBBBBBBB
BBBBBBBBBBBBB.....BBBBBBBBBBBB
BBBBBBBBBBBBB.....BBBBBBBBBBBB
BBBBBBBBBBBBB.....BBBBBBBBBBBB
BBBBBBBBBBBBB.....BBBBBBBBBBBB
BBBBBBBBBBBBB.....BBBBBBBBBBBB
BBBBBBBBBBBBB.....BBBBBBBBBBBB
`,
      metadata: { startsHigh: true, endsHigh: true },
    },

    // A laser gate built into a high plateau: the opening is just above the
    // floor and the wall runs off the top, so you time it on the plateau.
    highgate_d4: {
      layout: `
..............BB..............
..............BB..............
..............BB..............
..............BB..............
..............BB..............
..............BB..............
..............LL..............
..............LL..............
BBBBBBBBBBBBBBBBBBBBBBBBBBBBBB
BBBBBBBBBBBBBBBBBBBBBBBBBBBBBB
BBBBBBBBBBBBBBBBBBBBBBBBBBBBBB
BBBBBBBBBBBBBBBBBBBBBBBBBBBBBB
BBBBBBBBBBBBBBBBBBBBBBBBBBBBBB
BBBBBBBBBBBBBBBBBBBBBBBBBBBBBB
BBBBBBBBBBBBBBBBBBBBBBBBBBBBBB
BBBBBBBBBBBBBBBBBBBBBBBBBBBBBB
BBBBBBBBBBBBBBBBBBBBBBBBBBBBBB
BBBBBBBBBBBBBBBBBBBBBBBBBBBBBB
BBBBBBBBBBBBBBBBBBBBBBBBBBBBBB
BBBBBBBBBBBBBBBBBBBBBBBBBBBBBB
`,
      metadata: { startsHigh: true, endsHigh: true },
    },

    // Descend across a gap and down two terraces. Starts high, ends low.
    dropgap_d4: {
      layout: `
..............................
..............................
..............................
..............................
..............................
..............................
..............................
..............................
BBBBBBBBBB....................
BBBBBBBBBB....................
BBBBBBBBBB....................
BBBBBBBBBB....................
BBBBBBBBBB....................
BBBBBBBBBB...BBBBBBBBB........
BBBBBBBBBB...BBBBBBBBB........
BBBBBBBBBB...BBBBBBBBB........
BBBBBBBBBB...BBBBBBBBB........
BBBBBBBBBB...BBBBBBBBB........
BBBBBBBBBB...BBBBBBBBBBBBBBBBB
BBBBBBBBBB...BBBBBBBBBBBBBBBBB
`,
      metadata: { startsHigh: true, endsHigh: false },
    },
  },

  // --- level5: everything at once -----------------------------------------
  level5: {
    // The full reactor: a laser gate, then a corridor under stacked crossfire
    // with a pit to hop. Both lanes are backstopped right after the gate.
    reactor_e5: {
      layout: `
..............................
..............................
..............................
..............................
..............................
......BB......................
......BB......................
......BB......................
......BB......................
......BB......................
......BB......................
......BB......................
......BB......................
......BB..B...............pB..
......BB......................
......BB......................
......LL..B...................
......LL..B...............pB..
BBBBBBBBBBBBBBBBBB...BBBBBBBBB
BBBBBBBBBBBBBBBBBB...BBBBBBBBB
`,
      metadata: { startsHigh: false, endsHigh: false },
    },

    // A spiked climb with a gap in the middle: hop spikes on each tread and
    // clear the pit at the mid level. Ends high. (No fire — purely a precision
    // climb, which is what makes it an insane-tier piece.)
    overload_e5: {
      layout: `
..............................
..............................
..............................
..............................
..............................
..............................
..............................
.........................S....
......................BBBBBBBB
......................BBBBBBBB
......................BBBBBBBB
......................BBBBBBBB
.........S........S...BBBBBBBB
.......BBBBBB...BBBBBBBBBBBBBB
.......BBBBBB...BBBBBBBBBBBBBB
.......BBBBBB...BBBBBBBBBBBBBB
.......BBBBBB...BBBBBBBBBBBBBB
.......BBBBBB...BBBBBBBBBBBBBB
BBBBBBBBBBBBB...BBBBBBBBBBBBBB
BBBBBBBBBBBBB...BBBBBBBBBBBBBB
`,
      metadata: { startsHigh: false, endsHigh: true },
    },

    // Stacked crossfire over a pair of pits: dodge two bullet lanes while
    // hopping gaps. Both lanes backstopped; pits sit below the low lane.
    crossfirepits_e5: {
      layout: `
..............................
..............................
..............................
..............................
..............................
..............................
..............................
..............................
..............................
..............................
..............................
..............................
..............................
..BBB....................pB...
..............................
..............................
..BB..........................
..BB.....................pB...
BBBBBBBBBBBBBB..BBBB..BBBBBBBB
BBBBBBBBBBBBBB..BBBB..BBBBBBBB
`,
      metadata: { startsHigh: false, endsHigh: false },
    },
  },
};
